/** "The patient is here" — the decisions, in one testable place.
 *
 *  Arrival is a FLAG on a scheduled appointment, not a status. The appointment stays
 *  `scheduled` while the patient sits in the waiting room and becomes `completed` or
 *  `no_show` later. See the long note above ARRIVAL_UNDO_WINDOW_MINUTES in
 *  backend/server.py for why, and for what the patient portal's existing "check-in"
 *  actually is (a wellbeing questionnaire, unrelated to walking in).
 *
 *  The undo window is the SERVER's to enforce; these functions decide what to put on
 *  screen. They read `arrival_undo_until`, which the server computes and publishes with
 *  the row, so no client holds its own copy of the window length and drifts from it.
 */

/** Is this patient marked as arrived? */
export function isArrived(appt) {
  return Boolean(appt?.arrived_at);
}

/** May the Arrived button be offered?
 *
 *  Scheduled and not already marked. A cancelled or no-show visit is excluded here as
 *  well as server-side: a cancelled row sits in the same day column as the live ones and
 *  is exactly the one somebody clicks by mistake, and marking an attendance that did not
 *  happen is not a UI slip — it is a wrong clinical record.
 */
export function canMarkArrived(appt) {
  return appt?.status === "scheduled" && !isArrived(appt);
}

/** Is the undo still available?
 *
 *  A missing or unparseable `arrival_undo_until` reads as closed rather than open. The
 *  button simply is not offered, the server would refuse it anyway, and the alternative
 *  default offers an action that 400s.
 */
export function canUndoArrival(appt, now = Date.now()) {
  if (!isArrived(appt)) return false;
  const until = Date.parse(appt.arrival_undo_until || "");
  if (!Number.isFinite(until)) return false;
  return until > now;
}

/** Seconds left on the undo, floored at zero — for a countdown that does not go
 *  negative while a tab sits open. */
export function undoSecondsLeft(appt, now = Date.now()) {
  if (!isArrived(appt)) return 0;
  const until = Date.parse(appt.arrival_undo_until || "");
  if (!Number.isFinite(until)) return 0;
  return Math.max(0, Math.round((until - now) / 1000));
}

/** The countdown as a person reads it: "9:55", not "595s".
 *
 *  Found in a browser pass — a raw second count on a ten-minute window is a number
 *  nobody converts in their head, and it reads as a system detail leaking onto a button
 *  somebody is about to press in front of a patient.
 */
export function undoCountdown(appt, now = Date.now()) {
  const left = undoSecondsLeft(appt, now);
  const minutes = Math.floor(left / 60);
  const seconds = left % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}
